'use client';

import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from 'styled-components';
import { isCasinoTheme } from './calendar-stuff/CalendarPalette';
import { CASINO_THEME_STORAGE_KEY, isCasinoRoute, readCasinoTheme } from '@/utils/casinoTheme.mjs';

const CasinoThemeContext = createContext(null);

export function useCasinoTheme() {
  return useContext(CasinoThemeContext);
}

export default function CasinoThemeProvider({ children }) {
  const pathname = usePathname();
  const active = isCasinoTheme && isCasinoRoute(pathname);
  const [mode, setMode] = useState(readCasinoTheme);

  useLayoutEffect(() => {
    if (active) document.documentElement.dataset.casinoTheme = mode;
    else delete document.documentElement.dataset.casinoTheme;
  }, [active, mode]);

  useEffect(() => {
    const syncTheme = (event) => {
      if (event.key === CASINO_THEME_STORAGE_KEY || event.key === null) setMode(readCasinoTheme());
    };
    window.addEventListener('storage', syncTheme);
    return () => window.removeEventListener('storage', syncTheme);
  }, []);

  const context = useMemo(() => ({
    active,
    mode,
    toggleTheme() {
      const next = mode === 'dark' ? 'light' : 'dark';
      setMode(next);
      try { window.localStorage.setItem(CASINO_THEME_STORAGE_KEY, next); } catch {}
    },
  }), [active, mode]);
  const theme = useMemo(() => ({ calendarCasino: active, casinoMode: mode }), [active, mode]);

  return (
    <CasinoThemeContext.Provider value={context}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CasinoThemeContext.Provider>
  );
}
